"use client";

import { useRef } from "react";
import { animate, type MotionValue } from "framer-motion";
import { IOS_PUSH } from "@/src/shared/constants/animation";
import { listingService } from "../services";

interface ISwipe {
  x: number;
  y: number;
  width: number;
  dragging: boolean;
  lastX: number;
  lastT: number;
  velocity: number;
}

/**
 * The iPhone back swipe on a page: drag it right and it follows the finger (`progress`
 * 0 → 1 of the page's width), then finishes going back or springs home. Vertical moves
 * stay a scroll (the page sets `touch-action: pan-y`), and a drag that starts in a text
 * field is the field's.
 */
export function useSwipeBack(
  progress: MotionValue<number>,
  onBack: () => void,
) {
  const swipe = useRef<ISwipe | null>(null);

  const onPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest("input, textarea, [data-no-swipe]"))
      return;
    swipe.current = {
      x: e.clientX,
      y: e.clientY,
      width: e.currentTarget.offsetWidth || window.innerWidth,
      dragging: false,
      lastX: e.clientX,
      lastT: e.timeStamp,
      velocity: 0,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const s = swipe.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    if (!s.dragging) {
      if (Math.abs(e.clientY - s.y) > 10)
        swipe.current = null; // a scroll
      else if (dx > 10) {
        s.dragging = true;
        e.currentTarget.setPointerCapture(e.pointerId);
      }
      return;
    }
    const dt = e.timeStamp - s.lastT;
    if (dt > 0) s.velocity = (e.clientX - s.lastX) / dt;
    s.lastX = e.clientX;
    s.lastT = e.timeStamp;
    progress.set(Math.min(1, Math.max(0, dx / s.width)));
  };

  const onPointerEnd = () => {
    const s = swipe.current;
    swipe.current = null;
    if (!s?.dragging) return;
    if (listingService.swipeBackCommits(progress.get(), s.velocity)) {
      void animate(progress, 1, IOS_PUSH).then(onBack);
    } else {
      void animate(progress, 0, IOS_PUSH);
    }
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: onPointerEnd,
    onPointerCancel: onPointerEnd,
  };
}
